import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Shield as ShieldIcon,
  Warning as WarningIcon,
  BugReport as BugReportIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useApp } from '../context/AppContext';

const Security = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { websocketConnected } = useApp();
  const securityMetrics = {
    overallScore: 85,
    vulnerabilities: {
      critical: 0,
      high: 1,
      medium: 3,
      low: 5,
    },
    lastScan: '2 hours ago',
    status: 'Good',
  };

  const SecurityCard = ({ title, value, status, icon, color }) => (
    <Card sx={{ 
      background: 'rgba(255, 255, 255, 0.05)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      height: '100%',
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            background: `linear-gradient(45deg, ${color}20, ${color}40)`,
            border: `1px solid ${color}30`,
          }}>
            {icon}
          </Box>
          <Chip 
            label={status} 
            color={status === 'Secure' ? 'success' : status === 'Warning' ? 'warning' : 'error'}
            size="small"
          />
        </Box>
        
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'white', mb: 0.5 }}>
          {value}
        </Typography>
        
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
          Security Dashboard
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Security monitoring and vulnerability assessment
        </Typography>
      </Box>

      {/* Security Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SecurityCard
            title={t('security_score')}
            value={`${securityMetrics.overallScore}/100`}
            status={t('good')}
            icon={<ShieldIcon sx={{ color: '#43e97b' }} />}
            color="#43e97b"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SecurityCard
            title={t('critical_issues')}
            value={securityMetrics.vulnerabilities.critical}
            status={t('secure')}
            icon={<SecurityIcon sx={{ color: '#667eea' }} />}
            color="#667eea"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SecurityCard
            title={t('high_priority')}
            value={securityMetrics.vulnerabilities.high}
            status={t('security_warning')}
            icon={<WarningIcon sx={{ color: '#f093fb' }} />}
            color="#f093fb"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SecurityCard
            title={t('total_issues')}
            value={Object.values(securityMetrics.vulnerabilities).reduce((a, b) => a + b, 0)}
            status={t('monitor')}
            icon={<BugReportIcon sx={{ color: '#4facfe' }} />}
            color="#4facfe"
          />
        </Grid>
      </Grid>

      {/* Security Status */}
      <Card sx={{ 
        mb: 4,
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
            {t('security_status')}
          </Typography>
          
          <Alert 
            severity="success" 
            sx={{ 
              mb: 2,
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              border: '1px solid rgba(76, 175, 80, 0.3)',
            }}
          >
            {t('no_critical_vulnerabilities')}
          </Alert>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: 'white' }}>
                {t('overall_security_score')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'white' }}>
                {securityMetrics.overallScore}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={securityMetrics.overallScore} 
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(45deg, #43e97b, #38d9a9)',
                  borderRadius: 4,
                }
              }}
            />
          </Box>

          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {t('last_security_scan')}: {securityMetrics.lastScan}
          </Typography>
        </CardContent>
      </Card>

      {/* Vulnerability Breakdown */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: 'white', mb: 3 }}>
                {t('vulnerability_breakdown')}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#f44336' }} />
                    <Typography variant="body2" sx={{ color: 'white' }}>{t('critical')}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    {securityMetrics.vulnerabilities.critical}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ff9800' }} />
                    <Typography variant="body2" sx={{ color: 'white' }}>{t('high')}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    {securityMetrics.vulnerabilities.high}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ffeb3b' }} />
                    <Typography variant="body2" sx={{ color: 'white' }}>{t('medium')}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    {securityMetrics.vulnerabilities.medium}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#4caf50' }} />
                    <Typography variant="body2" sx={{ color: 'white' }}>{t('low')}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    {securityMetrics.vulnerabilities.low}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ 
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: 'white', mb: 3 }}>
                {t('security_actions')}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  startIcon={<SecurityIcon />}
                  fullWidth
                  onClick={() => alert(t('security_scan_initiated'))}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  {t('run_security_scan')}
                </Button>
                
                <Button 
                  variant="outlined" 
                  startIcon={<ShieldIcon />}
                  fullWidth
                  onClick={() => alert(t('security_policies_updated'))}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  {t('update_security_policies')}
                </Button>
                
                <Button 
                  variant="outlined" 
                  startIcon={<CheckCircleIcon />}
                  fullWidth
                  onClick={() => alert(t('generating_security_report'))}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  {t('view_security_report')}
                </Button>
                
                <Button 
                  variant="outlined" 
                  startIcon={<BugReportIcon />}
                  fullWidth
                  onClick={() => onNavigate && onNavigate('benchmarks')}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  {t('vulnerability_assessment')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Security;
